import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Code2, Coins, Copy, Lock, ShieldCheck, Terminal, UploadCloud, Zap } from "lucide-react";

const CLI_SNIPPET = `# Send any file from Linux / macOS / Windows terminal with LSC1 Zero-Knowledge:
npx @limeshare/cli send ./release-build.zip --burn

# Or verify an Ed25519 Certified Destruction Receipt via curl:
curl -s "https://api.limeshare.org/v1/transfers/4iKqZmPB7Ub3dZCB/receipt?sig=..." | jq .`;

const GITHUB_ACTION_SNIPPET = `name: Upload Encrypted Release Artifact
on: [release]
jobs:
  drop:
    runs-on: ubuntu-latest
    steps:
      - uses: actions/checkout@v4
      - name: Encrypt & Upload to LimeShare
        run: npx @limeshare/cli send ./dist/bundle.tar.gz --expire 7d`;

export function DevelopersPage() {
  const [copiedCli, setCopiedCli] = useState(false);

  const copyCli = async () => {
    await navigator.clipboard.writeText(CLI_SNIPPET);
    setCopiedCli(true);
    setTimeout(() => setCopiedCli(false), 2000);
  };

  return (
    <article className="panel legal" style={{ maxWidth: 880, margin: "0 auto" }}>
      <header style={{ marginBottom: 28 }}>
        <span className="badge" style={{ marginBottom: 12 }}>
          <Terminal size={13} /> Open LSC1 Protocol · transfer.sh Alternative
        </span>
        <h1 style={{ fontSize: "clamp(28px, 4.2vw, 42px)", fontWeight: 900, lineHeight: 1.12, margin: "0 0 12px" }}>
          LimeShare for Developers, CLI &amp; AI Agents
        </h1>
        <p className="hint" style={{ fontSize: 16, margin: 0 }}>
          The modern zero-knowledge successor to <code>transfer.sh</code> and <code>Firefox Send</code>. Stream encrypted artifacts from your terminal, CI/CD pipelines, or Model Context Protocol (MCP) AI agents.
        </p>
      </header>

      <section style={{ marginBottom: 30 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <h2 style={{ margin: 0, fontSize: 20 }}>Terminal CLI &amp; API Quickstart</h2>
          <button className="btn ghost btn-sm" onClick={copyCli}>
            {copiedCli ? <Check size={13} color="var(--lime)" /> : <Copy size={13} />}
            <span>{copiedCli ? "Copied" : "Copy Snippet"}</span>
          </button>
        </div>
        <pre className="preview-code">
          <code>{CLI_SNIPPET}</code>
        </pre>
      </section>

      <section style={{ marginBottom: 30 }}>
        <h2>GitHub Actions CI/CD Integration</h2>
        <pre className="preview-code">
          <code>{GITHUB_ACTION_SNIPPET}</code>
        </pre>
      </section>

      <section style={{ marginBottom: 30 }}>
        <h2>Open Discovery Endpoints (RFC 9727, MCP &amp; ARD)</h2>
        <table>
          <thead>
            <tr>
              <th>Standard</th>
              <th>Endpoint</th>
              <th>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>LSC1 Container Spec</b></td>
              <td><code>AES-256-GCM STREAM (4 MiB frames)</code></td>
              <td>Authenticated framing preventing truncation &amp; reordering</td>
            </tr>
            <tr>
              <td><b>API Catalog (RFC 9727)</b></td>
              <td><a href="/.well-known/api-catalog">/.well-known/api-catalog</a></td>
              <td>Automated REST API discovery linkset</td>
            </tr>
            <tr>
              <td><b>MCP Server Card</b></td>
              <td><a href="/.well-known/mcp/server-card.json">/.well-known/mcp/server-card.json</a></td>
              <td>Model Context Protocol tools for Claude, Cursor &amp; Agents</td>
            </tr>
            <tr>
              <td><b>LLM Knowledge Base</b></td>
              <td><a href="/llms-full.txt">/llms-full.txt</a></td>
              <td>Complete technical &amp; cryptographic specification</td>
            </tr>
          </tbody>
        </table>
      </section>
    </article>
  );
}

export function SellFilesPage() {
  return (
    <article className="panel legal" style={{ maxWidth: 880, margin: "0 auto" }}>
      <header style={{ marginBottom: 28 }}>
        <span className="badge" style={{ marginBottom: 12 }}>
          <Coins size={13} /> Zero-Knowledge Escrow · Pay-to-Decrypt Links
        </span>
        <h1 style={{ fontSize: "clamp(28px, 4.2vw, 42px)", fontWeight: 900, lineHeight: 1.12, margin: "0 0 12px" }}>
          Sell Digital Files, 4K Video &amp; 3D Assets with Pay-to-Decrypt Links
        </h1>
        <p className="hint" style={{ fontSize: 16, margin: 0 }}>
          Deliver client work without fear of non-payment. Your client can inspect the file list, verify exact sizes, and preview media in the browser — while full master download unlocks automatically upon payment.
        </p>
      </header>

      <div style={{ padding: 24, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", marginBottom: 32, textAlign: "center" }}>
        <h3 style={{ margin: "0 0 8px", fontSize: 20 }}>Create a Pay-to-Decrypt Link in 10 Seconds</h3>
        <p className="hint" style={{ margin: "0 0 16px" }}>
          On the homepage widget, click <b>Branding &amp; Paywall ▾</b>, enter your Studio Name and price in USD, and upload up to 5 GB free.
        </p>
        <Link to="/" className="btn" style={{ textDecoration: "none" }}>
          <UploadCloud size={16} /> Create Paywalled Transfer <ArrowRight size={15} />
        </Link>
      </div>

      <section style={{ marginBottom: 28 }}>
        <h2>Why Freelancers &amp; Studios Use LimeShare Escrow Instead of Gumroad or WeTransfer</h2>
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th style={{ color: "var(--lime)" }}>LimeShare Pay-to-Decrypt</th>
              <th>Gumroad</th>
              <th>Standard File Transfer</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>Account / KYC Required?</b></td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>None (Instant crypto checkout)</td>
              <td>Strict KYC &amp; bank holds</td>
              <td>N/A (No payment protection)</td>
            </tr>
            <tr>
              <td><b>Client Preview Before Paying</b></td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>Yes (In-browser player + file manifest)</td>
              <td>Static cover image only</td>
              <td>Client can steal full file before paying</td>
            </tr>
            <tr>
              <td><b>Zero-Knowledge Encryption</b></td>
              <td style={{ color: "var(--lime)", fontWeight: 700 }}>Yes (AES-256-GCM in browser)</td>
              <td>No</td>
              <td>No</td>
            </tr>
          </tbody>
        </table>
      </section>
    </article>
  );
}
